import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'

import {useViewImageContext} from './ViewImageContext'
import {useViewLandContext} from '../ViewLandContext'
import {usePayment} from '../../../../../../routing/Payment'
import {PLANET_HD_ACCESS_STATES, canUsePlanetHd} from './planetHdAccessModel'
import {getHdAvailableHa, getHdRequiredHa, IMAGE_TYPES_WITH_DATE} from './imageTypeUtils'
import {
  REMOTE_SENSING_LAYERS,
  REMOTE_SENSING_PRODUCTS,
  getImageTypeForSource,
  getProductLayerOptions,
  normalizeRemoteSensingSource,
} from './remoteSensingModel'

const PRODUCT_OPTIONS = [REMOTE_SENSING_PRODUCTS.SENTINEL, REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD]

const ProductFirstImageSelector = ({
  canCreateImageAmbient,
  openImageAmbient,
  requestDateSelection,
  dateSelector,
  preparingImage = false,
}) => {
  const {t} = useTranslation()
  const viewImageContext = useViewImageContext()
  const viewLandContext = useViewLandContext()
  const payment = usePayment()
  const land = viewLandContext.land
  const activeImage = viewImageContext.getActiveLandImage(land.land_id)
  const satelliteVisualizationActive = IMAGE_TYPES_WITH_DATE.has(activeImage.imageType)
  const imageLayerLoading = viewImageContext.getImageLayerLoadState?.(land.land_id)?.status === 'loading'
  const activeSource = useMemo(
    () => normalizeRemoteSensingSource(activeImage.imageType || 'rgb'),
    [activeImage.imageType]
  )
  const [requestedProduct, setRequestedProduct] = useState(activeSource.product)
  const [requestedLayer, setRequestedLayer] = useState(activeSource.layer)
  const [selectingProduct, setSelectingProduct] = useState(false)
  const hdAccess = viewImageContext.getHdAccessStatus(land.land_id)
  const landScope = `${land.company_id || ''}:${land.land_id}`
  const activeSelection = useRef(null)
  const selectionIntent = useRef(null)
  activeSelection.current = {
    landScope,
    source: activeSource,
    imageType: activeImage.imageType,
    selectedDate: activeImage.selectedDate,
  }
  const isCurrentSelection = useCallback(
    (intent) =>
      Boolean(intent) &&
      selectionIntent.current === intent &&
      activeSelection.current.landScope === intent.landScope,
    []
  )
  const {
    changeImageType,
    ensureLandHdAccess,
    getRememberedRemoteSensingSelection,
    loading,
    requestHdAccess,
  } =
    viewImageContext
  const product = requestedProduct
  const layers = getProductLayerOptions(product)
  const accessIsLoading = selectingProduct || hdAccess.loading ||
    hdAccess.state === PLANET_HD_ACCESS_STATES.PROVISIONING
  const hdUnavailable = product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD &&
    (!hdAccess.enabled || accessIsLoading)
  const planetHdNeedsEnablement = !hdAccess.enabled

  useEffect(() => {
    // Restored images own the selector unless the user is choosing another source.
    if (isCurrentSelection(selectionIntent.current)) {
      return
    }
    selectionIntent.current = null
    setRequestedProduct(activeSource.product)
    setRequestedLayer(activeSource.layer)
    setSelectingProduct(false)
  }, [activeSource.layer, activeSource.product, isCurrentSelection, landScope])

  useEffect(
    () => () => {
      selectionIntent.current = null
    },
    []
  )

  useEffect(() => {
    if (
      hdAccess.state !== PLANET_HD_ACCESS_STATES.PROVISIONING
    ) {
      return undefined
    }

    const refresh = () => {
      if (activeSelection.current.landScope !== landScope) {
        return
      }
      const intent = selectionIntent.current
      ensureLandHdAccess(land.land_id, {
        force: true,
        geometryHash: land.geometry_hash || land.geometryHash || '',
        operation: 'view_planet_hd',
        context: {product: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD},
      })
        .then((access) => {
          // Restored HD also refreshes access, but only an explicit choice may apply imagery.
          // A ready-state render can clean up this interval before this promise resolves.
          if (!isCurrentSelection(intent) || !canUsePlanetHd(access)) {
            return
          }

          const nextImageType = getImageTypeForSource({
            product: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
            layer: requestedLayer,
          })
          selectionIntent.current = null
          setSelectingProduct(false)
          if (activeSelection.current.imageType !== nextImageType) {
            const rememberedSelection = getRememberedRemoteSensingSelection?.(
              land.land_id,
              REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
            )
            const restoredSelection = changeImageType(land, nextImageType)
            if (
              restoredSelection?.selectedDate == null &&
              rememberedSelection?.selectedDate == null
            ) {
              requestDateSelection?.(REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD)
            }
          }
        })
        .catch(() => {})
    }

    const intervalId = window.setInterval(refresh, 5000)
    return () => {
      window.clearInterval(intervalId)
    }
  }, [
    hdAccess.state,
    land,
    landScope,
    requestedLayer,
    changeImageType,
    ensureLandHdAccess,
    getRememberedRemoteSensingSelection,
    isCurrentSelection,
    requestDateSelection,
  ])

  const applySource = (nextProduct, nextLayer) => {
    const imageType = getImageTypeForSource({product: nextProduct, layer: nextLayer})
    if (!imageType) {
      return false
    }

    const changedProduct = activeSelection.current.source.product !== nextProduct
    const rememberedSelection = changedProduct
      ? getRememberedRemoteSensingSelection?.(land.land_id, nextProduct)
      : activeSelection.current
    selectionIntent.current = null
    setSelectingProduct(false)
    const restoredSelection = activeSelection.current.imageType !== imageType
      ? changeImageType(land, imageType)
      : activeSelection.current
    // Each product restores its own selected date/scene. Changing the
    // visualization inside that product preserves the same selection too.
    const hasRestoredDate = restoredSelection?.selectedDate != null ||
      rememberedSelection?.selectedDate != null
    if (!hasRestoredDate) {
      requestDateSelection?.(nextProduct)
    }
    return true
  }

  const cancelSelection = (intent) => {
    if (!isCurrentSelection(intent)) {
      return
    }
    selectionIntent.current = null
    setRequestedProduct(activeSelection.current.source.product)
    setRequestedLayer(activeSelection.current.source.layer)
    setSelectingProduct(false)
  }

  const openPlanetHdPlanModal = useCallback(({access, error} = {}) => {
    const rawErrorData = error?.response?.data || {}
    const errorData = rawErrorData?.data || rawErrorData
    const paymentData = access?.data || access || errorData
    const requiredHa = getHdRequiredHa(paymentData)
    const availableHa = getHdAvailableHa(paymentData)
    const paymentCode = String(
      errorData.payment_code || errorData.code || errorData.reason ||
      paymentData?.payment_code || paymentData?.code || paymentData?.reason || ''
    ).toLowerCase()
    const hasBalanceData = [
      'available_hd_ha',
      'available_ha',
      'remaining_hd_ha',
      'remaining_ha',
      'assigned_hd_ha',
      'assigned_ha',
    ].some((key) => Object.prototype.hasOwnProperty.call(paymentData || {}, key))
    const isAccountCapacityIssue = error?.response?.status === 402 ||
      paymentCode.includes('payment') ||
      paymentCode.includes('planet_hd_ha') ||
      paymentCode.includes('not_enough') ||
      paymentCode === 'no_company_hd_period' ||
      paymentCode === 'insufficient_hd_ha' ||
      (hasBalanceData && requiredHa > availableHa + 0.01)

    if (!isAccountCapacityIssue) {
      return false
    }

    payment?.setActive?.({
      code: errorData.payment_code || errorData.code || errorData.reason || paymentData?.payment_code || paymentData?.code || paymentData?.reason || 'planet_hd_ha_required',
      data: {
        ...paymentData,
        available_ha: availableHa,
        operation: 'view_planet_hd',
        required_ha: requiredHa,
        resource_id: 'planet_hd_ha',
      },
      operation: 'view_planet_hd',
      resourceId: 'planet_hd_ha',
      source: 'planet_hd_lot_enablement',
    })
    return true
  }, [payment])

  const enablePlanetHd = async () => {
    if (selectingProduct || loading || hdAccess.loading) {
      return
    }

    const nextProduct = REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
    const nextLayers = getProductLayerOptions(nextProduct)
    const nextLayer = nextLayers.includes(requestedLayer) ? requestedLayer : nextLayers[0]
    const intent = {landScope}
    selectionIntent.current = intent
    setRequestedProduct(nextProduct)
    setRequestedLayer(nextLayer)
    setSelectingProduct(true)

    try {
      await requestHdAccess({
        land,
        operation: 'view_planet_hd',
        context: {product: nextProduct},
        onConfirmed: () =>
          isCurrentSelection(intent) ? applySource(nextProduct, nextLayer) : false,
        onUnavailable: (unavailable) => {
          const handled = openPlanetHdPlanModal(unavailable)
          if (handled) {
            cancelSelection(intent)
          }
          return handled
        },
      })
    } catch (error) {
      cancelSelection(intent)
    } finally {
      if (isCurrentSelection(intent)) {
        setSelectingProduct(false)
      }
    }
  }

  const selectProduct = (nextProduct) => {
    if (selectingProduct || loading) {
      return
    }

    const nextLayers = getProductLayerOptions(nextProduct)
    const nextLayer = nextLayers.includes(requestedLayer) ? requestedLayer : nextLayers[0]
    setRequestedProduct(nextProduct)
    setRequestedLayer(nextLayer)

    if (nextProduct === REMOTE_SENSING_PRODUCTS.SENTINEL) {
      applySource(nextProduct, nextLayer)
      return
    }

    if (hdAccess.enabled) {
      applySource(nextProduct, nextLayer)
    }
  }

  const selectLayer = (nextLayer) => {
    if (loading || hdUnavailable) {
      return
    }
    setRequestedLayer(nextLayer)
    applySource(product, nextLayer)
  }

  const getProductLabel = (option) =>
    option === REMOTE_SENSING_PRODUCTS.SENTINEL
      ? t('images:product.sentinel', {defaultValue: 'Sentinel'})
      : t('images:product.planet_hd', {defaultValue: 'Planet HD'})
  const getLayerHelp = (layer) => {
    if (layer === REMOTE_SENSING_LAYERS.VISIBLE) {
      return null
    }

    return t(`images:layer_help.${product}.${layer}`, {defaultValue: ''}) || null
  }
  const productSelector = (
    <div className='view-land-product-first' data-testid='remote-sensing-selector'>
      <div className='view-land-product-first__step'>
        <span className='view-land-product-first__label'>
          {t('images:product.label', {defaultValue: '1. Producto'})}
        </span>
        <div className='view-land-product-first__options'>
          {PRODUCT_OPTIONS.map((option) => {
            const planetOption = option === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
            const disabled = selectingProduct || loading || (planetOption && planetHdNeedsEnablement)
            return (
            <button
              key={option}
              type='button'
              onClick={() => selectProduct(option)}
              disabled={disabled}
              className={`view-land-image-chip ${product === option ? 'active' : ''} ${planetOption && planetHdNeedsEnablement ? 'hd-unavailable' : ''}`.trim()}
            >
              {getProductLabel(option)}
            </button>
            )
          })}
        </div>
        {planetHdNeedsEnablement && (
          <button
            type='button'
            className='btn btn-light-primary view-land-product-first__enable-hd'
            onClick={enablePlanetHd}
            disabled={selectingProduct || loading || hdAccess.loading || hdAccess.state === PLANET_HD_ACCESS_STATES.PROVISIONING}
          >
            {accessIsLoading && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />}
            {hdAccess.state === PLANET_HD_ACCESS_STATES.PROVISIONING
              ? t('images:hd_enablement.provisioning', {defaultValue: 'Habilitando Planet HD…'})
              : t('images:hd_enablement.action', {defaultValue: 'Habilitar Planet HD para este lote'})}
          </button>
        )}
        {product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD && !accessIsLoading && !hdAccess.enabled && hdAccess.state !== PLANET_HD_ACCESS_STATES.PREFLIGHT && (
          <div className={`view-land-product-first__access is-${hdAccess.state}`} role='status'>
            {t(`images:hd_enablement.states.${hdAccess.state}`, {
              defaultValue: t('images:hd_enablement.states.unavailable', {
                defaultValue: 'No pudimos habilitar Planet HD. Reintentá más tarde o contactanos.',
              }),
            })}
          </div>
        )}
      </div>

      <div className='view-land-product-first__step'>
        <span className='view-land-product-first__label'>
          {t('images:layer.label', {defaultValue: '2. Visualización'})}
        </span>
        <div className='view-land-product-first__options'>
          {layers.map((layer) => {
            const help = getLayerHelp(layer)
            const helpId = `image-layer-help-${product}-${layer}`
            const layerButton = (
              <button
                type='button'
                onClick={() => selectLayer(layer)}
                disabled={loading || hdUnavailable}
                aria-describedby={help ? helpId : undefined}
                className={`view-land-image-chip ${satelliteVisualizationActive && !hdUnavailable && requestedLayer === layer ? 'active' : ''}`.trim()}
              >
                {t(`images:layer.${layer}`, {
                  defaultValue:
                    layer === REMOTE_SENSING_LAYERS.VISIBLE ? 'Visible' : layer.toUpperCase(),
                })}
                {satelliteVisualizationActive && !hdUnavailable && requestedLayer === layer && (imageLayerLoading || preparingImage) &&
                  <span className='spinner-border spinner-border-sm ms-2' role='status'
                    aria-label={t('images:loading_image', {defaultValue: 'Cargando imagen'})} />}
              </button>
            )

            return (
              <span
                key={layer}
                className={`view-land-image-chip-help ${help ? 'has-help' : ''}`.trim()}
              >
                {help ? (
                  <OverlayTrigger
                    placement='top'
                    flip
                    overlay={(
                      <Tooltip id={helpId} className='view-land-image-chip-help__tooltip'>
                        {help}
                      </Tooltip>
                    )}
                  >
                    <span className='view-land-image-chip-help__target'>{layerButton}</span>
                  </OverlayTrigger>
                ) : layerButton}
              </span>
            )
          })}
        </div>
      </div>

      {canCreateImageAmbient && activeSource.layer === REMOTE_SENSING_LAYERS.EVI &&
        product === activeSource.product && requestedLayer === activeSource.layer &&
        !loading && !selectingProduct && !hdUnavailable && (
        <button type='button' className='btn btn-light-primary' onClick={openImageAmbient}>
          {t('images:buttons.create_ambientation')}
        </button>
      )}
    </div>
  )

  return <>{productSelector}{!hdUnavailable && dateSelector}</>
}

export default ProductFirstImageSelector
